import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import {
  CheckCircle, Circle, CaretRight, CaretDown, CaretUp, RocketLaunch,
  Buildings, PaintBrush, EnvelopeSimple, CreditCard, Truck, Storefront,
  Package, FolderOpen, FileText, Star
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const HIDE_KEY = 'magere_setup_checklist_collapsed';

const ITEM_META = {
  company: { icon: Buildings, label: 'Entreprise & coordonnées', desc: 'Raison sociale, téléphone, email, adresse — affichés sur le site et les emails', link: '/company' },
  identity: { icon: PaintBrush, label: 'Identité visuelle', desc: 'Logo, couleurs et réseaux sociaux de la boutique', link: '/identity' },
  smtp: { icon: EnvelopeSimple, label: 'Emails automatiques (SMTP)', desc: 'Confirmations de commande et notifications client — testez l\'envoi', link: '/notifications' },
  payments: { icon: CreditCard, label: 'Paiement en ligne', desc: 'Clés Stripe et/ou PayPal pour encaisser les commandes', link: '/payment-methods' },
  shipping: { icon: Truck, label: 'Zones de livraison', desc: 'Codes postaux, villes desservies et jours de livraison', link: '/shipping' },
  pickup: { icon: Storefront, label: 'Retrait en boutique', desc: 'Créneaux et retrait le jour même', link: '/shipping' },
  products: { icon: Package, label: 'Catalogue produits', desc: 'Au moins un produit en ligne avec prix et photo', link: '/products' },
  categories: { icon: FolderOpen, label: 'Images des catégories', desc: 'Une image par catégorie pour un accueil attrayant', link: '/categories' },
  pages: { icon: FileText, label: 'Pages du site', desc: 'CGV publiées et page contact complétée', link: '/pages' },
  review_link: { icon: Star, label: 'Lien avis Google', desc: 'Ajouté aux emails après livraison pour collecter des avis', link: '/company' },
};

export const SetupChecklist = ({ storeId, basePath, accentColor = '#c2185b' }) => {
  const [data, setData] = useState(null);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(HIDE_KEY) === '1');

  const fetchChecklist = useCallback(async () => {
    if (!storeId) return;
    try {
      const { data } = await axios.get(`${API_URL}/api/stores/${storeId}/setup-checklist`, { withCredentials: true });
      setData(data);
    } catch { /* ignore */ }
  }, [storeId]);

  useEffect(() => { fetchChecklist(); }, [fetchChecklist]);

  if (!data) return null;
  const pct = Math.round((data.completed / data.total) * 100);
  const allDone = data.completed === data.total;

  const toggle = () => {
    const next = !collapsed;
    setCollapsed(next);
    localStorage.setItem(HIDE_KEY, next ? '1' : '0');
  };

  if (collapsed) {
    return (
      <button onClick={toggle} data-testid="setup-checklist-expand"
        className="flex items-center gap-2.5 px-4 py-2 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 transition-colors text-sm shadow-sm">
        <RocketLaunch size={16} weight="fill" style={{ color: accentColor }} />
        <span className="font-semibold text-slate-700">Mise en route</span>
        <span className="text-[11px] font-bold px-2 py-0.5 rounded-full text-white" style={{ backgroundColor: allDone ? '#10b981' : accentColor }}>
          {data.completed}/{data.total}
        </span>
        <CaretDown size={14} className="text-slate-400" />
      </button>
    );
  }

  return (
    <Card className="overflow-hidden border-slate-200" data-testid="setup-checklist">
      <div className="px-5 py-4 flex items-center justify-between" style={{ backgroundColor: accentColor + '0d' }}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: accentColor + '1f' }}>
            <RocketLaunch size={22} weight="fill" style={{ color: accentColor }} />
          </div>
          <div>
            <h2 className="font-bold text-slate-900 text-[15px]">Mise en route de votre boutique</h2>
            <p className="text-xs text-slate-500">
              {allDone ? '🎉 Tout est configuré, votre boutique est prête !' : `${data.completed} étape${data.completed > 1 ? 's' : ''} sur ${data.total} — suivez le guide pour tout régler.`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="hidden sm:flex items-center gap-2">
            <div className="w-28 h-2 rounded-full bg-slate-200 overflow-hidden">
              <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: allDone ? '#10b981' : accentColor }} />
            </div>
            <span className="text-xs font-bold" style={{ color: allDone ? '#10b981' : accentColor }}>{pct}%</span>
          </div>
          <Button variant="ghost" size="sm" onClick={toggle} className="h-8 px-2 text-slate-400" data-testid="setup-checklist-collapse" title="Réduire">
            <CaretUp size={16} />
          </Button>
        </div>
      </div>

      <CardContent className="p-0">
        <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 divide-slate-100">
          {data.items.map((item, idx) => {
            const meta = ITEM_META[item.key];
            if (!meta) return null;
            const Icon = meta.icon;
            return (
              <Link key={item.key} to={`${basePath}${meta.link}`} data-testid={`setup-item-${item.key}`}
                className={`flex items-center gap-3 px-5 py-3 hover:bg-slate-50 transition-colors group ${idx % 2 === 0 ? 'md:border-r md:border-slate-100' : ''} ${idx >= 2 ? 'md:border-t md:border-slate-100' : ''}`}>
                {item.done
                  ? <CheckCircle size={20} weight="fill" className="text-emerald-500 flex-shrink-0" />
                  : <Circle size={20} className="text-slate-300 flex-shrink-0" />}
                <Icon size={17} className={item.done ? 'text-slate-400' : 'text-slate-500'} />
                <div className="flex-1 min-w-0">
                  <p className={`text-[13px] font-semibold ${item.done ? 'text-slate-400 line-through decoration-slate-300' : 'text-slate-800'}`}>{meta.label}</p>
                  {!item.done && <p className="text-[11px] text-slate-400 truncate">{meta.desc}</p>}
                </div>
                {!item.done && (
                  <span className="text-[11px] font-semibold flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" style={{ color: accentColor }}>
                    Configurer <CaretRight size={12} />
                  </span>
                )}
              </Link>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};
